import type { ScheduleBar, ScheduleCar } from '../types.js';
import { type ScheduleView, type TimeRange, ticksFor } from './dropLogic.js';

/**
 * 317 (#632) — how busy each car is, and how many cars are free in each column, computed from the
 * schedule response the grid already draws (research R1). Pure: no DOM, no clock, no zone — epoch
 * milliseconds throughout, and column bounds come from `dropLogic` in Malaysia time.
 */

export interface Interval {
  start: number;
  end: number;
}

export interface BarClass {
  /** Time held by a rental that counts toward "% booked". */
  booked: boolean;
  /** The car cannot take another booking during it — never free. */
  occupied: boolean;
  /** A block: taken out of the time the car could be booked. */
  blocked: boolean;
}

/**
 * 🔴 R2 — "booked" is decided by the BOOKING's status, never by the hold's `state`. The backend
 * calls a hold `committed` only for confirmed / checked out / checked in, so a completed rental that
 * keeps its hold (311) is labelled tentative, or "Abandoned?" after a day; reading `state` would
 * count finished rentals as unbooked. Unpaid holds occupy the car but are not booked (Q2).
 */
const BOOKED_STATUSES: ReadonlySet<string> = new Set([
  'confirmed',
  'checked_out',
  'checked_in',
  'completed',
]);

export function classifyBar(bar: ScheduleBar): BarClass {
  if (bar.state === 'unheld') return { booked: false, occupied: false, blocked: false };
  if (bar.kind === 'booking' || bar.kind === 'extension') {
    const booked = bar.booking_status !== null && BOOKED_STATUSES.has(bar.booking_status);
    return { booked, occupied: true, blocked: false };
  }
  return { booked: false, occupied: true, blocked: true };
}

/** The part of `iv` inside `range`, or null when nothing of it is (touching an edge is nothing). */
export function clip(iv: Interval, range: TimeRange): Interval | null {
  const start = Math.max(iv.start, range.start);
  const end = Math.min(iv.end, range.end);
  return end > start ? { start, end } : null;
}

/** Sorted, merged: overlapping or touching intervals become one, so overlap counts once. */
export function union(ivs: readonly Interval[]): Interval[] {
  const sorted = [...ivs].sort((a, b) => a.start - b.start);
  const out: Interval[] = [];
  for (const iv of sorted) {
    const last = out[out.length - 1];
    if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end);
    else out.push({ ...iv });
  }
  return out;
}

export function totalMs(ivs: readonly Interval[]): number {
  return ivs.reduce((sum, iv) => sum + (iv.end - iv.start), 0);
}

/** `from` minus `remove`; both are unions (sorted, disjoint). */
export function subtract(from: readonly Interval[], remove: readonly Interval[]): Interval[] {
  const out: Interval[] = [];
  for (const iv of from) {
    let pieces: Interval[] = [{ ...iv }];
    for (const r of remove) {
      pieces = pieces.flatMap((p) => {
        if (r.end <= p.start || r.start >= p.end) return [p];
        const left = r.start > p.start ? [{ start: p.start, end: r.start }] : [];
        const right = r.end < p.end ? [{ start: r.end, end: p.end }] : [];
        return [...left, ...right];
      });
    }
    out.push(...pieces);
  }
  return out;
}

/** Half-open: `[a, b)` touches `[start, end)` only when `a < end && b > start`. */
export function overlaps(iv: Interval, start: number, end: number): boolean {
  return iv.start < end && iv.end > start;
}

/** A bar's own interval (the server's instants, unclipped). */
export function barInterval(bar: ScheduleBar): Interval {
  return { start: Date.parse(bar.start_at), end: Date.parse(bar.end_at) };
}

export type OccupancyKind = 'measured' | 'blocked' | 'unavailable';

export interface CarOccupancy {
  carId: string;
  kind: OccupancyKind;
  bookedMs: number;
  /** Blocked time that is not also booked. */
  blockedMs: number;
  /** The time the car could be booked: the view minus `blockedMs`. */
  capacityMs: number;
  /** `bookedMs / capacityMs` in [0, 1]; null unless `kind` is `measured`. */
  ratio: number | null;
  label: string;
}

/**
 * R4 — whole percent, except that any booked time shows at least "<1%" and anything short of full
 * never rounds up to 100%: an idle car and a nearly idle one, a full car and a nearly full one, must
 * not read the same.
 */
export function percentLabel(ratio: number): string {
  if (ratio <= 0) return '0% booked';
  if (ratio >= 1) return '100% booked';
  const pct = Math.round(ratio * 100);
  if (pct < 1) return '<1% booked';
  if (pct >= 100) return '99% booked';
  return `${pct}% booked`;
}

/**
 * US1 (FR-001 to FR-006) — one car's booked share of the view. `bars` may include other cars'
 * bars; only this car's are read.
 */
export function carOccupancy(
  car: ScheduleCar,
  bars: readonly ScheduleBar[],
  range: TimeRange,
): CarOccupancy {
  const length = range.end - range.start;
  if (car.unavailable) {
    return {
      carId: car.id,
      kind: 'unavailable',
      bookedMs: 0,
      blockedMs: 0,
      capacityMs: 0,
      ratio: null,
      label: 'Unavailable',
    };
  }
  const booked: Interval[] = [];
  const blocked: Interval[] = [];
  for (const bar of bars) {
    if (bar.car_id !== car.id) continue;
    const cls = classifyBar(bar);
    const iv = clip(barInterval(bar), range);
    if (!iv) continue;
    if (cls.booked) booked.push(iv);
    if (cls.blocked) blocked.push(iv);
  }
  const bookedU = union(booked);
  const blockedOnly = subtract(union(blocked), bookedU);
  const bookedMs = totalMs(bookedU);
  const blockedMs = totalMs(blockedOnly);
  const capacityMs = length - blockedMs;
  if (capacityMs <= 0) {
    return {
      carId: car.id,
      kind: 'blocked',
      bookedMs,
      blockedMs,
      capacityMs: 0,
      ratio: null,
      label: 'Blocked',
    };
  }
  const ratio = Math.min(1, bookedMs / capacityMs);
  return {
    carId: car.id,
    kind: 'measured',
    bookedMs,
    blockedMs,
    capacityMs,
    ratio,
    label: percentLabel(ratio),
  };
}

export interface FreeCar {
  id: string;
  label: string;
  plate: string;
}

export interface ColumnAvailability {
  start: number;
  end: number;
  /** Cars shown that are not unavailable (a blocked car counts here and is simply not free). */
  eligible: number;
  free: number;
  freeCars: FreeCar[];
  /** No eligible car is free — and there is at least one to count. */
  full: boolean;
}

/**
 * US2 (FR-007 to FR-009) — per column of the view (the axis ticks: hours in Day, days in Week and
 * Month), how many cars are free for the WHOLE column. Anything that occupies a car (a booking
 * hold of any status, or a block) overlapping any part of the column makes it not free; the overlap
 * is half-open, so a booking ending at midnight leaves the next day free.
 */
export function columnAvailability(
  cars: readonly ScheduleCar[],
  bars: readonly ScheduleBar[],
  range: TimeRange,
  view: ScheduleView,
): ColumnAvailability[] {
  const occupiedByCar = new Map<string, Interval[]>();
  for (const bar of bars) {
    if (!classifyBar(bar).occupied) continue;
    const iv = clip(barInterval(bar), range);
    if (!iv) continue;
    const list = occupiedByCar.get(bar.car_id);
    if (list) list.push(iv);
    else occupiedByCar.set(bar.car_id, [iv]);
  }
  const eligibleCars = cars.filter((c) => !c.unavailable);
  const ticks = ticksFor(range, view);
  return ticks.map((tick, i) => {
    const start = tick.at;
    const end = ticks[i + 1]?.at ?? range.end;
    const freeCars = eligibleCars
      .filter((c) => !(occupiedByCar.get(c.id) ?? []).some((iv) => overlaps(iv, start, end)))
      .map((c) => ({ id: c.id, label: c.label, plate: c.plate_number }));
    return {
      start,
      end,
      eligible: eligibleCars.length,
      free: freeCars.length,
      freeCars,
      full: eligibleCars.length > 0 && freeCars.length === 0,
    };
  });
}

export interface OccupancySummary {
  cars: Record<string, CarOccupancy>;
  columns: ColumnAvailability[];
}

/** Everything the screen shows, computed once per schedule response (R6). */
export function occupancySummary(
  cars: readonly ScheduleCar[],
  bars: readonly ScheduleBar[],
  range: TimeRange,
  view: ScheduleView,
): OccupancySummary {
  const byCar = new Map<string, ScheduleBar[]>();
  for (const bar of bars) {
    const list = byCar.get(bar.car_id);
    if (list) list.push(bar);
    else byCar.set(bar.car_id, [bar]);
  }
  const summary: OccupancySummary = {
    cars: {},
    columns: columnAvailability(cars, bars, range, view),
  };
  for (const car of cars) summary.cars[car.id] = carOccupancy(car, byCar.get(car.id) ?? [], range);
  return summary;
}
